// Faixa global "Conciliação Bancária" (DA-214) — substitui o antigo AlertaPixRecebidoBanner.
// Aparece em QUALQUER tela com a aba Conciliação fechada: no balcão ninguém deixa a aba aberta o
// dia todo, então o aviso tem que ser do sistema, não da tela.
//
// Conta tudo que falta conciliar de QUALQUER data (princípio DA-213 / feedback_aviso_pendencia_
// financeira_todas_datas): Pix capturado não-conferido + cartão capturado não-conferido + vendas
// finalizadas SEM recebimento (Desconhecido). É dinheiro: estilo Contas a Pagar (fundo #fde68a +
// letra preta, daltonismo-safe), NÃO some sozinha, sem dispensar — só sai quando tudo for conciliado/vinculado.
// Visível pra TODO mundo logado (balcão): a atendente precisa saber que o Pix caiu pra ir vincular a
// venda; clicar leva a /caixa aba Conciliação, onde até não-admin pode Vincular (podeVincular). O BEEP
// de Pix novo também toca pra todos. (Antes era admin/gerente only — Ruan pediu pra ver no balcão.)
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wallet } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useSocket } from '../lib/socket';
import { formatMoeda } from '@fiodecobre/shared';
import { tocarBeep, destravarAutoplay } from '../modules/atendimento/lib/beep';
import { useAlertaFaixa } from './alertas/AlertasContexto';

interface Pendentes {
  quantidade: number;
  total: string;
  desconhecidos: number;
  // Cerco do Pix (DA-248): vendas finalizadas SEM nota, esperando o pagamento confirmar pra emitir.
  sem_nota?: number;
}

export function AlertaConciliacaoBanner() {
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const { socket } = useSocket();
  const [pendentes, setPendentes] = useState<Pendentes | null>(null);
  // Estornos de cartão pendentes (DA-280) — só admin vê/age. Segmento próprio na mesma faixa.
  const [estornos, setEstornos] = useState(0);
  const jaBipados = useRef<Set<string>>(new Set());

  const logado = !!usuario;
  // A FAIXA e o beep aparecem pra todo mundo logado (sinal de balcão — a atendente precisa saber que
  // o Pix caiu pra ir vincular a venda; a vinculação não é restrita a admin).
  const podeVer = logado;

  // Destrava o autoplay de áudio (Chrome/Safari bloqueiam som até interagir com a aba).
  useEffect(() => {
    if (!logado) return;
    const unlock = () => destravarAutoplay();
    document.addEventListener('click', unlock);
    document.addEventListener('keydown', unlock);
    document.addEventListener('pointerdown', unlock);
    return () => {
      document.removeEventListener('click', unlock);
      document.removeEventListener('keydown', unlock);
      document.removeEventListener('pointerdown', unlock);
    };
  }, [logado]);

  async function recarregar() {
    if (!podeVer) return;
    try { setPendentes(await api.get<Pendentes>('/api/conciliacao/pendentes')); }
    catch { /* aviso é opcional */ }
    // Estornos pendentes são conteúdo só-admin (o endpoint é admin/gerente; a ação é só-admin).
    if (usuario?.perfil === 'admin') {
      try { const rows = await api.get<unknown[]>('/api/cartao/estornos-pendentes'); setEstornos(rows.length); }
      catch { /* opcional */ }
    }
  }

  useEffect(() => { if (logado) void recarregar(); }, [logado, podeVer]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!socket || !logado) return;
    const onNovo = (p: { registro?: { mp_payment_id?: string; status?: string }; novo?: boolean }) => {
      const id = p.registro?.mp_payment_id;
      if (p.novo && p.registro?.status === 'approved' && id && !jaBipados.current.has(id)) {
        jaBipados.current.add(id);
        try { tocarBeep(); } catch { /* autoplay pode estar travado */ }
      }
      void recarregar();
    };
    const onAtualizado = () => { void recarregar(); };
    socket.on('mp:pix_recebido', onNovo);
    socket.on('mp:pix_atualizado', onAtualizado);
    socket.on('cartao:aprovado', onAtualizado);
    socket.on('cartao:estorno_pendente', onAtualizado);
    return () => {
      socket.off('mp:pix_recebido', onNovo);
      socket.off('mp:pix_atualizado', onAtualizado);
      socket.off('cartao:aprovado', onAtualizado);
      socket.off('cartao:estorno_pendente', onAtualizado);
    };
  }, [socket, logado]); // eslint-disable-line react-hooks/exhaustive-deps

  const ehAdmin = usuario?.perfil === 'admin';
  const qtd = pendentes?.quantidade ?? 0;
  const temEstorno = ehAdmin && estornos > 0;

  const ativo = !!podeVer && (qtd > 0 || temEstorno);
  const recolhido = useAlertaFaixa(
    'conciliacao',
    // Estorno entra na contagem: sao dois segmentos da MESMA faixa, e a pastilha resume a faixa.
    ativo
      ? { rotulo: 'Conciliação', bg: '#fde68a', borda: '#ca8a04', qtd: qtd + (temEstorno ? estornos : 0) }
      : null,
  );

  if (!ativo || recolhido) return null;

  return (
    <div
      style={{
        background: '#fde68a', borderLeft: '4px solid #ca8a04', color: '#000',
        padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, cursor: 'pointer',
      }}
      onClick={() => navigate('/caixa', { state: { aba: 'conciliacao' } })}
      title="Abrir a aba Conciliação Bancária"
    >
      <Wallet size={16} />
      {qtd > 0 && pendentes && (
        <>
          <span style={{ fontWeight: 700 }}>Conciliação Bancária:</span>
          <span>
            {qtd} {qtd === 1 ? 'recebimento aguardando conciliação' : 'recebimentos aguardando conciliação'} ({formatMoeda(pendentes.total)})
            {pendentes.desconhecidos > 0 && <> · <strong>{pendentes.desconhecidos} sem recebimento</strong></>}
            {!!pendentes.sem_nota && pendentes.sem_nota > 0 && (
              <> · <strong>{pendentes.sem_nota} venda{pendentes.sem_nota === 1 ? '' : 's'} sem nota (aguardando pagamento)</strong></>
            )}
          </span>
        </>
      )}
      {temEstorno && (
        <span style={{ fontWeight: 700 }}>
          {qtd > 0 ? '· ' : ''}{estornos} estorno{estornos === 1 ? '' : 's'} de cartão pendente{estornos === 1 ? '' : 's'} (venda cancelada)
        </span>
      )}
      <span style={{ marginLeft: 'auto', textDecoration: 'underline', fontWeight: 600 }}>Conferir</span>
    </div>
  );
}
